import { Link } from 'react-router-dom';
import { ArrowRight, ShieldAlert, ListChecks, PieChart, Clipboard, Activity, HardHat, FileText } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function ServicesRiskManagement() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Services · Risk management</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Connect AI initiatives to enterprise risk frameworks
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            We help risk and compliance teams map AI-specific threats and controls into the registers and committees you
            already run—so “model risk” is not a parallel universe.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Overview Section */}
          <div className="space-y-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Service Overview</h2>
            <p className="text-[#5c5c5c] leading-relaxed max-w-4xl">
              RakFort's Risk Management services bridge the gap between technical AI risks and corporate governance. 
              We provide the tools and expertise to translate model-level vulnerabilities into enterprise-level 
              risk registers, ensuring that AI risk is managed with the same rigor and transparency as any other 
              critical business function.
            </p>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Categories / Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Outcomes</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Shared Language</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Developing a common taxonomy for IT, security, legal, and business teams to discuss residual risk effectively.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <ListChecks className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Traceable Registers</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Ensuring full traceability from assessment outputs (ScanFort) to specific treatment plans and owners.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Board-Ready Summaries</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Producing high-level reporting that stays proportionate to materiality and informs strategic decision-making.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Why Us - Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Why Choose RakFort?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <ShieldAlert className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Framework Expertise</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Deep experience in mapping AI risks to standards like ISO 31000, NIST AI RMF, and internal ERM frameworks.
                </p>
              </div>
              <div className="space-y-3">
                <PieChart className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Data-Driven Insights</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Our risk scoring is based on empirical testing data, not just subjective assessments.
                </p>
              </div>
              <div className="space-y-3">
                <HardHat className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Proactive Mitigation</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  We don't just identify risks; we provide concrete, actionable treatment plans for remediation.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Our Approach */}
          <div className="rf-card p-8 md:p-10 bg-white border-dashed border-2">
            <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Our Engagement Approach</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">01</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Risk Inventory</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Establishing a comprehensive inventory of all AI models and initiatives across the organization.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">02</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Control Mapping</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Mapping existing enterprise controls to AI-specific threats and identifying necessary new controls.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">03</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Continuous Monitoring</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Setting up automated feeds from ScanFort and other tools to keep risk registers updated in real-time.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">04</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Governance Integration</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Embedding AI risk reporting into existing committee structures and executive decision cycles.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/riskfort" className="text-[#005a9c] font-medium hover:text-[#004a82] mr-3">
              See RiskFort
            </Link>
            <Link to="/contactus" className="text-[#005a9c] font-medium hover:text-[#004a82] inline-flex items-center gap-1">
              Engage us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </RfPageFrame>
      </section>
    </>
  );
}
